/*
 * The module's sidebar sections (spec 12 §4.5, UX §1.2): the shell draws the chrome, chat supplies
 * the rows.
 *
 * A row says five things at once — what kind of conversation it is, what it is called, whether
 * anything in it is unread, whether a draft is waiting, and whether it was asked to keep quiet — and
 * all of them but the kind are in its accessible name too, because bold text, a badge and grey are
 * not information to a screen reader ([25] A2). A direct row with one person in it says a sixth: the
 * glyph of whatever status that person published, whose words ride in the accessible name with the
 * rest. Which conversation is *open* is said with `aria-current="page"` and its own surface, never
 * with the weight that means unread: one signal per fact (A-07 §3.8).
 *
 * A row names its conversation the way every other surface names a place: a channel is `#slug`,
 * which is what the address bar, the composer and the team all say (X-A3 V21). The display name is
 * for describing the channel — the details pane, the browse row's second line — and never for
 * pointing at it.
 *
 * Only conversations this account is actually in are listed (A-01 §4.2), and a row a person has put
 * away is not listed either: both facts are their membership row's, and `sidebarOrder`
 * (state/selectors.ts) is where they are read. The three sections are filters over that one order and
 * never orders of their own — that is what lets the sidebar, `Alt+↑`/`Alt+↓` and the quick switcher
 * agree about where a conversation stands. The one way to another conversation of a kind sits in the
 * section's heading row, which is core's (`SidebarSection.Actions`).
 */

import { useCallback, useRef, useState, useSyncExternalStore } from "react"
import { keyOf, useDraftStore, type DraftRecord } from "../../../core/drafts/index.ts"
import { formatCapped } from "../../../core/i18n/format.ts"
import { useNow } from "../../../core/i18n/useNow.ts"
import { accountBadge } from "../../../core/people/person.ts"
import { PersonFace } from "../../../core/people/PersonChip.tsx"
import { StatusMark } from "../../../core/people/PersonStatus.tsx"
import { isTeamMember } from "../../../core/people/standing.ts"
import { statusName, statusOf, type Status } from "../../../core/people/status.ts"
import { Icon, inPlaceClick, useActiveConversation } from "../../../core/shell/index.ts"
import type { ConversationId, Model } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { cx } from "../../../lib/cx.ts"
import { Button, emojiText, Menu } from "../../../ui/index.ts"
import { conversationAddress, conversationTitle } from "../conversation/title.ts"
import { t } from "../i18n/t.ts"
import { conversationPath, dmPath } from "../navigation.ts"
import { useNewMessageRequest } from "../shortcuts.ts"
import { badgeCountOf, chatSlice, isDirect, isSilenced, sidebarOrder } from "../state/selectors.ts"
import { CountCap, type MemberSelf } from "../wire/chat.gen.ts"
import type { ConversationView } from "../wire/views.ts"
import { useBrowseChannels } from "./browseChannels.ts"
import { BrowseChannelsDialog } from "./BrowseChannelsDialog.tsx"
import { ConversationMenu, opensMenu } from "./ConversationMenu.tsx"
import { NewChannelDialog } from "./NewChannelDialog.tsx"
import { NewDirectMessageDialog } from "./NewDirectMessageDialog.tsx"
import "./sidebar.css"

/** The pencil and the crossed bell beside a name; both facts are in the row's accessible name too. */
const ROW_MARK_PX = 14

/**
 * Which rows belong to which section. A starred conversation is drawn once, in *Starred*, and not a
 * second time under its kind — a row in two places is two chances to lose track of one conversation.
 */
const SECTIONS = {
	starred: (_kind: string, starred: boolean) => starred,
	channels: (kind: string, starred: boolean) => !starred && !isDirect(kind),
	directs: (kind: string, starred: boolean) => !starred && isDirect(kind),
} as const

type SectionName = keyof typeof SECTIONS

interface RowProps {
	conv: ConversationView
	/** This reader's own membership row: the star, the mute, the level the menu reads and writes. */
	self: MemberSelf
	/** How the row names the place: "#allgemein", or the people of a direct conversation. */
	label: string
	/** What the conversation is called — the face of a direct one is drawn from it. */
	title: string
	/** What the badge claims: the unread count, or the mentions alone in a silent conversation. */
	badge: number
	/**
	 * Whether the count names the reader — a mention in it, or an unread direct conversation, which is
	 * somebody writing to them and nobody else. That count is the accent pill; a plain unread count is
	 * the neutral one, read by its weight (VISUAL.md §7.2, D-06 Q2).
	 */
	mine: boolean
	/** Muted, or switched off for good: the row is grey, sorted last and quiet about its unread. */
	silenced: boolean
	/** Whether a folded section still draws this row: the current conversation, or one that names you. */
	pinned: boolean
	/** The other people in a direct conversation, so a row of one can show that person's face. */
	otherIds: number[]
	/** That person's picture, when they have uploaded one: the row draws it instead of initials. */
	otherAvatar?: string | undefined
	/**
	 * What that person published about themselves, on a direct row with exactly one other person in
	 * it. A group has no one status to draw, and a channel has none at all.
	 */
	otherStatus?: Status | null | undefined
	/** Their claim to be away, which is the face's dot and outlives the status above. */
	otherAway?: boolean | undefined
	/**
	 * The pill of that person's account — *Guest* above all: a direct conversation with somebody
	 * outside the team says so on its row, where one decides what to write.
	 */
	otherBadge?: string | undefined
}

/** The mark before the name: a private channel's lock, a person's picture. */
function RowGlyph({
	conv,
	title,
	otherIds,
	otherAvatar,
	otherAway,
}: Pick<RowProps, "conv" | "title" | "otherIds" | "otherAvatar" | "otherAway">) {
	// A channel's hash is part of its name — the row reads "#allgemein" — so it is not drawn twice.
	// The empty slot stays, and with it the one line every name in the sidebar starts on.
	if (conv.kind === "private") return <Icon name="lock" />
	if (conv.kind === "channel") return <span aria-hidden="true" className="tb-conv-row__glyph" />
	// A group carries no one face, so it wears the group mark rather than an arbitrary member's.
	if (otherIds.length !== 1) return <Icon name="people" />
	// The product's one face (core/people/PersonChip.tsx), which is also where the presence dot comes
	// from. No hover card: a sidebar is scanned, and a card opening under the pointer on the way to
	// the third row down would be in the way of the one thing the row is for.
	return (
		<PersonFace
			person={{ id: otherIds[0] ?? 0, name: title, avatarUrl: otherAvatar, away: otherAway }}
			size="xs"
			hover={false}
		/>
	)
}

/**
 * The row's accessible name. Each combination of a count and a draft is its own key rather than a
 * sentence stitched together from translated fragments, because a fragment has no grammar to be
 * translated by. Above the server's cap the count is no longer a number but a bound, and the name
 * says so: "999 unread" would be an exact claim about a channel holding two thousand (X-A2 D11).
 *
 * Two facts wrap instead of multiplying that table, for the same reason: a status wraps the name,
 * because it is about the person and everything after it is about the conversation, and being muted
 * wraps the whole finished row and comes last, because it is the least of the six.
 */
function rowLabel(
	name: string,
	badge: number,
	hasDraft: boolean,
	silenced: boolean,
	status: Status | null | undefined,
): string {
	// The status joins the *name* rather than the finished row, because it is a fact about the person
	// and the counts are facts about the conversation: "Bea Bergmann, In a meeting, 2 unread" is the
	// order the row is read in. The glyph itself carries nothing here — the link's own accessible name
	// replaces everything inside it — so this is the only way the words reach a screen reader.
	const named =
		status === null || status === undefined
			? name
			: t("chat.sidebar.status", { name, status: statusName(status) })
	const row = countedLabel(named, badge, hasDraft)
	return silenced ? t("chat.sidebar.muted", { row }) : row
}

function countedLabel(name: string, badge: number, hasDraft: boolean): string {
	if (badge >= CountCap) return t("chat.sidebar.unreadMany", { name, count: CountCap })
	if (badge > 0 && hasDraft) return t("chat.sidebar.unreadWithDraft", { name, count: badge })
	if (badge > 0) return t("chat.sidebar.unread", { name, count: badge })
	if (hasDraft) return t("chat.sidebar.withDraft", { name })
	return name
}

/** True while something is waiting to be sent in this conversation's own box. */
function hasContent(draft: DraftRecord | undefined): boolean {
	if (draft === undefined) return false
	return draft.markdown.trim() !== "" || draft.attachments.length > 0
}

/**
 * Whether this conversation holds a draft, read from the local draft store — the one that has it.
 * The server's mirror is what the model carries, and it arrives seconds later and never at all
 * while the box is still being typed in, which is why the pill never appeared (X-A1 D7).
 */
function useHasDraft(conv: ConversationId): boolean {
	const store = useDraftStore()
	const subscribe = useCallback((listener: () => void) => store.subscribe(listener), [store])
	const key = keyOf({ kind: "conversation", conv })
	return useSyncExternalStore(subscribe, () => hasContent(store.get(key)))
}

function ConversationRow({
	conv,
	self,
	label,
	title,
	badge,
	mine,
	silenced,
	otherIds,
	otherAvatar,
	otherStatus,
	otherAway,
	otherBadge,
}: RowProps) {
	const active = useActiveConversation() === conv.id
	const hasDraft = useHasDraft(conv.id)
	// The row opens its own menu, because the row is what has focus and what the pointer is over; the
	// `⋯` is the pointer's way in and no tab stop of its own (ConversationMenu.tsx).
	const [menuOpen, setMenuOpen] = useState(false)
	// The row's own link, so the menu can give the focus back to it: it is the single tab stop of the
	// row, and the `⋯` is not drawn at all once the focus has left (ConversationMenu.tsx).
	const rowRef = useRef<HTMLAnchorElement>(null)
	const path = isDirect(conv.kind) ? dmPath(conv.dmHandle ?? "") : conversationPath(conv.slug ?? "")
	const classes = ["tb-conv-row"]
	if (badge > 0) classes.push("tb-conv-row--unread")
	if (silenced) classes.push("tb-conv-row--muted")
	if (active) classes.push("tb-conv-row--current")
	return (
		<li className={classes.join(" ")}>
			<a
				ref={rowRef}
				href={path}
				// The whole name is the tooltip as well: a 256 px column ellipsises
				// `#release-notes-and-deployments`, and what it cuts has to be readable somewhere.
				title={label}
				aria-label={rowLabel(
					otherBadge === undefined ? label : `${label}, ${otherBadge}`,
					badge,
					hasDraft,
					silenced,
					otherStatus,
				)}
				aria-current={active ? "page" : undefined}
				onClick={inPlaceClick(path)}
				onKeyDown={(event) => {
					if (!opensMenu(event)) return
					event.preventDefault()
					setMenuOpen(true)
				}}
				onContextMenu={(event) => {
					// The gesture everybody tries first. The browser's own menu is given up deliberately:
					// what it offers for a link — copy the address — this menu offers by name.
					event.preventDefault()
					setMenuOpen(true)
				}}
			>
				<RowGlyph
					conv={conv}
					title={title}
					otherIds={otherIds}
					otherAvatar={otherAvatar}
					otherAway={otherAway}
				/>
				<span className="tb-conv-row__name">{emojiText(label)}</span>
				{otherBadge === undefined ? null : <span className="tb-pill">{otherBadge}</span>}
				{/* The glyph alone, against the name: a 256 px column has no room for the words, and they
				    are already in the row's accessible name above and in this mark's own tooltip. */}
				<StatusMark status={otherStatus} />
				{silenced ? (
					<span className="tb-conv-row__silent">
						<Icon name="bellOff" size={ROW_MARK_PX} />
					</span>
				) : null}
				{hasDraft ? (
					<span className="tb-conv-row__draft">
						<Icon name="edit" size={ROW_MARK_PX} />
					</span>
				) : null}
				{badge > 0 ? (
					<span
						className={cx(
							"tb-pill",
							mine ? "tb-pill--mention" : "tb-pill--count",
							"tb-conv-row__badge",
						)}
					>
						{formatCapped(badge, CountCap)}
					</span>
				) : null}
			</a>
			<ConversationMenu
				conv={conv}
				self={self}
				label={label}
				path={path}
				open={menuOpen}
				onOpenChange={setMenuOpen}
				focusRow={() => rowRef.current?.focus()}
			/>
		</li>
	)
}

/**
 * The rows of one section, in the one order every surface reads (`sidebarOrder`).
 *
 * It is a plain function over the model, because the shell asks the same question in two ways: a
 * component renders the rows, and the section's heading asks how many a fold is hiding and whether
 * there are any at all — and those two are selectors over the model, not components (`registry.ts`).
 * One derivation, three callers, and no chance of a heading that disagrees with the list under it.
 */
function rowsOf(
	model: Model,
	section: SectionName,
	now: number,
	current: ConversationId | null,
): RowProps[] {
	return buildRows(
		{
			order: sidebarOrder(model),
			conversations: model.conversations,
			users: model.users,
			meId: model.me?.id ?? 0,
			self: chatSlice(model).self,
		},
		section,
		now,
		current,
	)
}

/** What a row is made of, as the five slices of the model it is read from. */
interface RowSource {
	order: readonly ConversationId[]
	conversations: Model["conversations"]
	users: Model["users"]
	meId: number
	self: Record<number, MemberSelf>
}

function buildRows(
	source: RowSource,
	section: SectionName,
	now: number,
	current: ConversationId | null,
): RowProps[] {
	const { conversations, users, meId, self } = source
	const belongs = SECTIONS[section]
	const rows: RowProps[] = []
	for (const id of source.order) {
		const slice = conversations[id]
		const membership = self[id]
		if (!slice || membership === undefined) continue
		const conv = slice.meta as ConversationView
		if (!belongs(conv.kind, membership.starred)) continue
		const silenced = isSilenced(membership, now)
		// The picture and the status are read here and not in the glyph: the glyph answers three kinds
		// of conversation before it reaches a face, and a read inside it would run for every channel
		// as well. Only a conversation with exactly one other person in it has one of each — a group
		// has no single face and no single status.
		const otherIds = (conv.memberIds ?? []).filter((member) => member !== meId)
		const other = otherIds.length === 1 ? users[otherIds[0] ?? 0] : undefined
		rows.push({
			conv,
			self: membership,
			label: conversationAddress(conv, users, meId),
			title: conversationTitle(conv, users, meId),
			badge: badgeCountOf(slice, silenced),
			// Somebody writing to you alone is a mention by another route: a direct conversation with
			// anything unread in it names you as surely as an `@` does.
			mine: slice.mentions > 0 || (isDirect(conv.kind) && slice.unread > 0),
			silenced,
			// Which rows survive a folded section: where the reader is, and anything that names them.
			pinned: current === conv.id || slice.mentions > 0,
			otherIds,
			otherAvatar: other?.avatarUrl,
			otherStatus: statusOf(other),
			otherAway: other?.statusAway === true,
			otherBadge: other === undefined ? undefined : accountBadge(other),
		})
	}
	return rows
}

/**
 * The same rows for a component. The five slices are subscribed to one at a time, each a selection
 * that keeps its identity while nothing it depends on moved — a single selector returning the built
 * array would hand the store a fresh object on every read and never settle. One clock for the whole
 * list, on the minute (`useNow`).
 */
function useRows(section: SectionName): RowProps[] {
	const order = useModel(sidebarOrder)
	const conversations = useModel((model) => model.conversations)
	const users = useModel((model) => model.users)
	const meId = useModel((model) => model.me?.id ?? 0)
	const self = useModel((model) => chatSlice(model).self)
	const now = useNow()
	const current = useActiveConversation()
	return buildRows({ order, conversations, users, meId, self }, section, now, current)
}

/**
 * Which rows a folded section still draws: the conversation the reader is in, so folding never hides
 * where they are, and every conversation that names them, so folding never hides a mention (Slack's
 * rule, VISUAL.md §7.2). The heading then says how many rows it is keeping out of sight.
 */
function visible(rows: RowProps[], collapsed: boolean): RowProps[] {
	return collapsed ? rows.filter((row) => row.pinned) : rows
}

function List({ section, collapsed }: { section: SectionName; collapsed: boolean }) {
	const rows = visible(useRows(section), collapsed)
	// A section nobody has put anything into is not a section: *Starred* appears with the first star
	// and leaves with the last, and a column where every section is empty shows the shell's one way
	// out instead of a sentence per section (`useIsEmpty`, `Door`).
	if (rows.length === 0) return null
	return (
		<ul className="tb-conv-list">
			{rows.map((row) => (
				<ConversationRow key={row.conv.id} {...row} />
			))}
		</ul>
	)
}

/**
 * The browse dialog, and for a team member the New channel dialog beside it: the pair every door to
 * another channel mounts. A guest makes no channel (the server refuses it), so for a guest the New
 * channel dialog is never mounted and Browse's empty state offers no *New channel*.
 */
function ChannelDialogs({
	teamMember,
	creating,
	setCreating,
	browse,
}: {
	teamMember: boolean
	creating: boolean
	setCreating: (open: boolean) => void
	browse: ReturnType<typeof useBrowseChannels>
}) {
	return (
		<>
			{teamMember ? <NewChannelDialog open={creating} onOpenChange={setCreating} /> : null}
			<BrowseChannelsDialog
				open={browse.open}
				onOpenChange={browse.setOpen}
				{...(teamMember
					? {
							onCreate: () => {
								browse.setOpen(false)
								setCreating(true)
							},
						}
					: {})}
			/>
		</>
	)
}

/**
 * The ways to another channel, as one control in the section's heading row (UX §1.2, X-A3 V15) — one
 * line of chrome per section instead of two. For a team member it is a `+` with two entries, *New
 * channel* and *Browse channels*; for a guest, who makes no channel, it is the one entry that is left,
 * drawn as the button itself rather than as a menu of one. The dialogs are mounted here because this
 * is what is on screen wherever the section is, Home's own *Browse channels* action included — and in
 * the one shape of the column where this row is not drawn at all, by `ChannelDoor` below.
 */
export function ChannelActions() {
	const [creating, setCreating] = useState(false)
	const browse = useBrowseChannels()
	const teamMember = useModel((model) => isTeamMember(model.me))
	return (
		<>
			{teamMember ? (
				<Menu
					trigger={
						<Button
							variant="ghost"
							quiet
							icon
							size="sm"
							aria-label={t("chat.sidebar.addChannel")}
							before={<Icon name="plus" size={16} />}
						/>
					}
					items={[
						{
							id: "new",
							label: t("chat.sidebar.newChannel"),
							icon: "plus",
							onSelect: () => setCreating(true),
						},
						{
							id: "browse",
							label: t("chat.sidebar.browse"),
							icon: "search",
							onSelect: () => browse.setOpen(true),
						},
					]}
				/>
			) : (
				<Button
					variant="ghost"
					quiet
					icon
					size="sm"
					aria-label={t("chat.sidebar.browse")}
					before={<Icon name="search" size={16} />}
					onClick={() => browse.setOpen(true)}
				/>
			)}
			<ChannelDialogs {...{ teamMember, creating, setCreating, browse }} />
		</>
	)
}

/**
 * The one way out of a sidebar with nothing in it (VISUAL.md §7.17): the shell draws one empty state
 * for the whole column and asks the first section that offers a door for it, so the dialogs travel
 * with the buttons.
 *
 * **Joining comes before founding** (N5-R1-4). An invited member arrives in a place that already has
 * channels, and offering *New channel* as the one thing to do asked them to found a second #allgemein
 * on their first minute; *Browse channels* is the door that leads where the team already is, and
 * making a channel is the quiet second option behind it — a team member's only: a guest makes none.
 *
 * This is also the other of the two places the browse dialog is mounted: the column draws either its
 * sections or this door and never both, and the section's heading control (`ChannelActions`) is off
 * screen in exactly the case that matters — a reader in no conversation at all, whose Home offers
 * *Browse channels* through the `homeActions` slot. Without the dialog here that action set a flag
 * nothing was listening to.
 */
export function ChannelDoor() {
	const [creating, setCreating] = useState(false)
	const browse = useBrowseChannels()
	const teamMember = useModel((model) => isTeamMember(model.me))
	return (
		<div className="tb-actions">
			<Button
				variant="primary"
				label={t("chat.sidebar.browse")}
				onClick={() => browse.setOpen(true)}
			/>
			{teamMember ? (
				<Button
					variant="ghost"
					label={t("chat.sidebar.newChannel")}
					onClick={() => setCreating(true)}
				/>
			) : null}
			<ChannelDialogs {...{ teamMember, creating, setCreating, browse }} />
		</div>
	)
}

/** The conversations this person keeps at the top, whatever kind they are (X-A4 N3). */
export function StarredRows({ collapsed }: { collapsed: boolean }) {
	return <List section="starred" collapsed={collapsed} />
}

export function ChannelRows({ collapsed }: { collapsed: boolean }) {
	return <List section="channels" collapsed={collapsed} />
}

/** The one way to another direct conversation, in its section's heading row. */
export function DirectMessageActions() {
	// `Mod+N` opens this same dialog from anywhere in the application, the way Home's action opens the
	// browse one (`../shortcuts.ts`); the button below is its other door.
	const { open: opening, setOpen: setOpening } = useNewMessageRequest()
	return (
		<>
			<Button
				variant="ghost"
				quiet
				icon
				size="sm"
				aria-label={t("chat.sidebar.newDm")}
				before={<Icon name="plus" size={16} />}
				onClick={() => setOpening(true)}
			/>
			<NewDirectMessageDialog open={opening} onOpenChange={setOpening} />
		</>
	)
}

export function DirectMessageRows({ collapsed }: { collapsed: boolean }) {
	return <List section="directs" collapsed={collapsed} />
}

/*
 * What the section's heading asks of its rows: how many a fold is hiding, and whether there are any
 * at all. Both are selectors over the model, read by core through one `useModel` each so that the
 * heading repaints exactly when the answer changes (`core/modules/registry.ts`). The clock is read
 * here rather than handed in: a mute that has just run out un-silences a row, and a heading that
 * counted it as silent for another minute would disagree with the list under it.
 */
function hiddenIn(model: Model, section: SectionName, active: ConversationId | null): number {
	const rows = rowsOf(model, section, Date.now(), active)
	return rows.length - visible(rows, true).length
}

export const starredHidden = (model: Model, active: ConversationId | null) =>
	hiddenIn(model, "starred", active)
export const channelsHidden = (model: Model, active: ConversationId | null) =>
	hiddenIn(model, "channels", active)
export const directsHidden = (model: Model, active: ConversationId | null) =>
	hiddenIn(model, "directs", active)

export const starredEmpty = (model: Model) =>
	rowsOf(model, "starred", Date.now(), null).length === 0
export const channelsEmpty = (model: Model) =>
	rowsOf(model, "channels", Date.now(), null).length === 0
export const directsEmpty = (model: Model) =>
	rowsOf(model, "directs", Date.now(), null).length === 0
